// Must come first: installs crypto.getRandomValues, which Hermes does not
// provide and the HPKE key transfer needs before it can generate a key pair.
import '../src/utils/randomPolyfill';

import { useEffect } from 'react';
import { ActivityIndicator, Platform, View, useColorScheme } from 'react-native';
import { Slot, useRouter, useSegments } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { ThemeProvider as NavThemeProvider } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useAuthStore } from '../src/stores/authStore';
import { useEncryptionStore } from '../src/stores/encryptionStore';
import { useNetworkStore } from '../src/stores/networkStore';
import { useNotificationStore } from '../src/stores/notificationStore';
import { useTeamStore } from '../src/stores/teamStore';
import { syncTeamKeys } from '../src/utils/teamKeySync';
import { deriveX25519KeyPair } from '../src/utils/teamCrypto';
import { getSeed } from '../src/utils/crypto';
import ConfirmDialogHost from '../src/components/ConfirmDialogHost';
import { settingsApi, userApi } from '../src/api/client';
import { useDiagnosticsStore } from '../src/stores/diagnosticsStore';
import { useAiSettingsStore } from '../src/stores/aiSettingsStore';
import { ThemeProvider, useTheme } from '../src/theme/ThemeContext';
import { useThemeStore } from '../src/stores/themeStore';
import { createNavigationTheme } from '../src/theme/navigationTheme';
import {
  initSentry,
  setSentryUser,
  clearSentryUser,
  setVersionStatus,
} from '../src/observability/sentry';
import { currentVersion } from '../src/utils/appVersion';
import { useVersionStatus } from '../src/hooks/useVersionStatus';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, asyncStoragePersister } from '../src/api/queryClient';
import AppErrorBoundary from '../src/components/AppErrorBoundary';
import { onSessionChange } from '../src/utils/sessionBroadcast';

// Initialise Sentry at module scope so it captures errors during first render.
// Also installs global error handlers (ErrorUtils.setGlobalHandler on RN,
// window.onerror + unhandledrejection on web).
initSentry({
  release: currentVersion(),
  environment: process.env.NODE_ENV ?? (__DEV__ ? 'development' : 'production'),
  dist: Platform.OS,
});

// Replay the last known diagnostics preference before anyone signs in. The
// account setting is authoritative but unreadable without a token, so this is
// the only thing standing between an opted-out account and an ungoverned launch
// window. Fire-and-forget: the store defaults to "report" until this resolves.
useDiagnosticsStore.getState().hydrateFromCache();

export default function RootLayout() {
  return (
    // SafeAreaProvider must sit outside ThemeProvider: the theme exposes
    // headerPaddingTop, which is derived from the top inset, so the insets have
    // to be readable by the time the theme is built.
    <SafeAreaProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister: asyncStoragePersister }}
      >
        <ThemeProvider>
          <AppErrorBoundary>
            <RootLayoutInner />
          </AppErrorBoundary>
        </ThemeProvider>
      </PersistQueryClientProvider>
    </SafeAreaProvider>
  );
}

function RootLayoutInner() {
  const theme = useTheme();
  const systemScheme = useColorScheme();
  const themeMode = useThemeStore((s) => s.mode);
  const isDark = themeMode === 'dark' || (themeMode === 'system' && systemScheme === 'dark');
  const navTheme = createNavigationTheme(theme.colors, isDark);

  const initializeAuth = useAuthStore((s) => s.initialize);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const initializeEncryption = useEncryptionStore((s) => s.initialize);
  const reinitializeForUser = useEncryptionStore((s) => s.reinitializeForUser);
  const encryptionInitialized = useEncryptionStore((s) => s.isInitialized);
  const e2eeSetupComplete = useEncryptionStore((s) => s.e2eeSetupComplete);
  const hasLocalKey = useEncryptionStore((s) => s.hasLocalKey);
  const router = useRouter();
  const segments = useSegments() as unknown as string[];

  useEffect(() => {
    initializeAuth();
    initializeEncryption();
    const cleanupNetwork = useNetworkStore.getState().initialize();
    return cleanupNetwork;
  }, [initializeAuth, initializeEncryption]);

  // Mounted HERE, at the root, rather than only on the screens that display it:
  // the point is to tag the telemetry session, and a status known only to
  // someone who happened to open Settings would tag almost nothing. The About
  // and Contact screens reuse this same cached query.
  //
  // 'unknown' is deliberately not reported. Leaving the attribute absent is what
  // keeps those sessions in the alert's field of view — see setVersionStatus.
  const { status: versionStatus } = useVersionStatus();
  useEffect(() => {
    if (versionStatus !== 'unknown') setVersionStatus(versionStatus);
  }, [versionStatus]);

  // Re-initialize encryption when a different user logs in, or when
  // currentUserId goes stale (e.g. forgetDevice() during account reset nulls
  // it while the user stays authenticated with the same user.id).
  const currentUserId = useEncryptionStore((s) => s.currentUserId);
  useEffect(() => {
    if (user?.id) {
      reinitializeForUser(user.id);
    }
  }, [user?.id, currentUserId, reinitializeForUser]);

  // Sync team keys + fetch teams after auth + e2ee are ready so that
  // decryption has the team AES keys and personalTeamId available.
  useEffect(() => {
    if (isAuthenticated && user?.id && e2eeSetupComplete && hasLocalKey) {
      (async () => {
        try {
          const seed = await getSeed(user.id);
          if (seed) {
            const { privateKey } = deriveX25519KeyPair(seed);
            await syncTeamKeys(privateKey);
          }
        } catch (error) {
          console.error('Failed to sync team keys on init:', error);
        }
      })();
    }
  }, [isAuthenticated, user?.id, e2eeSetupComplete, hasLocalKey]);

  // Initialize notification polling after auth + e2ee are ready
  useEffect(() => {
    if (isAuthenticated && user?.id && e2eeSetupComplete) {
      const cleanupNotifications = useNotificationStore.getState().initialize();
      return cleanupNotifications;
    } else if (!isAuthenticated) {
      useNotificationStore.getState().clear();
    }
  }, [isAuthenticated, user?.id, e2eeSetupComplete]);

  // Sync user context with Sentry for error attribution
  useEffect(() => {
    if (isAuthenticated && user) {
      setSentryUser(user);
    } else {
      clearSentryUser();
    }
  }, [isAuthenticated, user]);

  // Apply the account's diagnostics preference as soon as there is a session to
  // ask with. This lives here rather than on the settings screen because the
  // preference has to take effect for users who never open settings — reading
  // it only there would leave telemetry ungoverned for almost everyone.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    settingsApi
      .get()
      .then((res) => {
        if (cancelled) return;
        useDiagnosticsStore.getState().loadFromSettings(res.data);
        // Same reason, for AI: the store starts at aiEnabled=false, so until
        // something loads it every AI surface says "AI features are disabled"
        // to a user who enabled them. Only the settings screens used to load
        // it, so the message flipped depending on whether they had been opened.
        useAiSettingsStore.getState().loadFromSettings(res.data);
      })
      // Offline or a failing /settings must not change what the user chose:
      // the cached value from hydrateFromCache stays in force.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  // Cross-tab session isolation (web only; no-op on native).
  // When another tab logs in as a different user (or logs out), force this tab
  // to log out so it doesn't continue operating under the wrong identity.
  useEffect(() => {
    const unsubscribe = onSessionChange((broadcastUserId) => {
      const currentUser = useAuthStore.getState().user;
      const currentUserId = currentUser?.id ?? null;

      // ⚠️  LOCALLY, never `logout()`. Every tab shares the one session cookie
      //     (src/auth/webSession.ts), and `logout()` revokes whatever it holds
      //     NOW — after a switch of user, that is the session the other tab
      //     just created. The tab that signed out has already done the server
      //     half; this one only clears itself.
      if (broadcastUserId === null) {
        // Another tab logged out — this tab follows
        if (currentUserId !== null) {
          useAuthStore.getState().forgetSessionLocally();
        }
      } else if (currentUserId !== null && broadcastUserId !== currentUserId) {
        // Another tab logged in as a different user
        useAuthStore.getState().forgetSessionLocally();
      }
    });

    return unsubscribe;
  }, []);

  // Safety net: if authenticated but user profile never loads (getMe failed
  // during login), retry once and force logout after 10s to avoid an infinite spinner.
  useEffect(() => {
    if (!isAuthenticated || user) return;

    let cancelled = false;
    const retryFetch = async () => {
      try {
        const resp = await userApi.getMe();
        if (!cancelled) {
          await useAuthStore.getState().setUser(resp.data);
        }
      } catch {
        // Timeout below will handle it
      }
    };
    retryFetch();

    const timeout = setTimeout(() => {
      if (!cancelled && !useAuthStore.getState().user) {
        // Only force logout if we're online — if the server is down or
        // device is offline, keep the user authenticated with stale tokens.
        const { connectivityState } = useNetworkStore.getState();
        if (connectivityState === 'online') {
          useAuthStore.getState().logout();
        }
      }
    }, 10_000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isLoading || !encryptionInitialized) return;

    const inAuthGroup = segments[0] === '(auth)';
    const inSetupGroup = segments[0] === '(setup)';

    if (!isAuthenticated) {
      // ⚠️  /auth/callback MUST be exempt, and for a sharp reason. It is
      //     the OIDC redirect target, so it is reached with the authorization
      //     code in the URL and the user necessarily NOT yet authenticated —
      //     which is precisely the condition this branch redirects on. Without
      //     the exemption the redirect fires before the callback screen can read
      //     the code, the code is discarded, and the login appears to fail
      //     silently while the identity provider did everything right. That is
      //     not hypothetical: it swallowed a real code on 2026-08-02.
      const onOidcCallback = segments[0] === 'auth' && segments[1] === 'callback';
      if (!inAuthGroup && !onOidcCallback) {
        router.replace('/(auth)/login');
      }
      return;
    }

    // Wait for user profile to load before making routing decisions that
    // depend on user.e2ee_setup_complete or user.id (for reinitializeForUser).
    // Without this guard, there is a flash of the restore screen on re-login
    // because hasLocalKey hasn't been re-derived yet.
    if (!user) return;

    // The passkey-enrollment gate used to sit here, ahead of E2EE setup. It is
    // gone with Phase 4: enrollment happens at the identity provider's hosted
    // login, which issues no token until a credential exists, so there is no
    // half-enrolled session for the app to intercept.

    const e2eeComplete = (e2eeSetupComplete || user?.e2ee_setup_complete) && hasLocalKey;
    if (!e2eeComplete) {
      // Allow free navigation within E2EE setup screens (restore, reset-warning,
      // reset-confirm, transfer-target, encryption, complete, etc.).
      const onE2EESetupScreen = inSetupGroup;
      if (!onE2EESetupScreen) {
        // ⚠️  ASK THE SERVER WHETHER A KEY EVER EXISTED — do not infer it from
        //     whether this login happened to create the account.
        //
        //     `restore` is only reachable-from-here when there is something to
        //     restore: the user completed setup on another device, so the
        //     recovery phrase can rebuild the key. When the server says setup
        //     was never completed there is no phrase in existence, and sending
        //     someone there asks for 24 words that have never been generated.
        //
        //     This used to read `isNewUser ? 'encryption' : 'restore'`, seeded
        //     from the `created` flag of the ONE login that provisioned the row
        //     (completeOidcLogin). Any first login whose window died after
        //     provisioning but before setup — the popup 401s on 2026-08-04 —
        //     left no client having seen `created: true`, so every later login
        //     routed to /restore permanently. The account was reachable only by
        //     deleting its row. e2ee_setup_complete is the same fact, held
        //     somewhere that survives a lost window.
        const targetScreen = user.e2ee_setup_complete ? 'restore' : 'encryption';
        router.replace(`/(setup)/${targetScreen}`);
      }
      return;
    }

    const inTabsGroup = segments[0] === '(tabs)';
    // The source device is fully set up, so without this exemption the layout
    // bounces it straight back to the tabs mid-transfer.
    const onTransferSource = inSetupGroup && segments[1] === 'transfer-source';
    if (!inTabsGroup && !onTransferSource) {
      router.replace('/(tabs)/activities');
    }
  }, [isLoading, encryptionInitialized, isAuthenticated, e2eeSetupComplete, hasLocalKey, user?.e2ee_setup_complete, segments]);

  if (isLoading || !encryptionInitialized) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.colors.bgSecondary }}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <StatusBar style={isDark ? 'light' : 'dark'} />
      </View>
    );
  }

  return (
    <NavThemeProvider value={navTheme}>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <Slot />
      <ConfirmDialogHost />
    </NavThemeProvider>
  );
}
